/**
 * Delivery fee: what the venue offers, and how the server reports the result.
 *
 * The guest only ever says WHERE — a map pin plus the address the courier reads. The server
 * matches the pin to one of the venue's areas, prices it, puts a shipping line on the order
 * and includes it in `total`. Nothing here computes a fee, a minimum or a total; the areas
 * are read only to centre the map and draw the circles.
 */

export interface LatLng {
  lat: number;
  lng: number;
}

/** What the app sends as top-level `delivery_location` on a cart update. */
export interface DeliveryLocation extends LatLng {
  address: string;
}

export interface DeliveryArea {
  id: string;
  name: string;
  radiusKm: number;
}

/** The venue's delivery pricing, from the shop payload's `delivery` key. */
export interface DeliverySettings {
  enabled: boolean;
  venue: LatLng | null;
  areas: DeliveryArea[];
}

export type DeliveryStatus = 'ok' | 'below_minimum' | 'out_of_range' | 'not_offered';

/** The server's answer for the order's location: `feather_extras.delivery`. */
export interface DeliveryQuote {
  status: DeliveryStatus;
  areaName: string | null;
  missingForMinimum: number | null;
  missingForFree: number | null;
  location: DeliveryLocation | null;
}

/** The 422 codes a SUBMIT is refused with. The order is not placed and comes back in the body. */
export const DELIVERY_SUBMIT_REFUSALS = [
  'delivery_out_of_range',
  'delivery_below_minimum',
  'delivery_location_required',
] as const;

const DELIVERY_OFF: DeliverySettings = { enabled: false, venue: null, areas: [] };

const STATUSES: DeliveryStatus[] = ['ok', 'below_minimum', 'out_of_range', 'not_offered'];

/**
 * Read `clientData.delivery`. An older backend sends no key at all, which is simply "off" —
 * and so is an "on" without a venue position, since there would be nothing to price from.
 */
export function parseDeliverySettings(clientData: any): DeliverySettings {
  const delivery = clientData?.delivery;
  if (!delivery || typeof delivery !== 'object' || delivery.enabled !== true) {
    return DELIVERY_OFF;
  }

  const venue = parseLatLng(delivery.venue);
  if (!venue) {
    return DELIVERY_OFF;
  }

  const areas = (Array.isArray(delivery.areas) ? delivery.areas : [])
    .map((a: any): DeliveryArea => ({
      id: String(a?.id ?? ''),
      name: String(a?.name ?? ''),
      radiusKm: Number(a?.radius_km),
    }))
    .filter((a: DeliveryArea) => isFinite(a.radiusKm) && a.radiusKm > 0);

  return { enabled: true, venue, areas };
}

/**
 * The delivery fee on an order, or null when the order has no delivery line.
 *
 * Our line is the shipping line with meta `_feather_extra = delivery`; a WooCommerce order
 * may carry other shipping lines, which are not ours and are ignored. ⚠️ 0 is a real answer
 * ("Free delivery") and must stay distinct from null ("no delivery on this order").
 */
export function parseOrderDeliveryFee(order: any): number | null {
  if (!order || typeof order !== 'object') return null;

  const line = (Array.isArray(order.shipping_lines) ? order.shipping_lines : []).find((l: any) =>
    metaValue(l?.meta_data, '_feather_extra') === 'delivery'
  );
  if (!line) return null;

  const fee = parseFloat(line.total);
  return isNaN(fee) || fee < 0 ? 0 : fee;
}

/**
 * `feather_extras.delivery`, or null when the order has no location.
 *
 * Decoded leniently, as the contract asks: an unknown status or a malformed number never
 * throws, because a decode failure here must not lose the order around it.
 */
export function parseDeliveryQuote(order: any): DeliveryQuote | null {
  const block = order?.feather_extras?.delivery;
  if (!block || typeof block !== 'object') return null;

  const status: DeliveryStatus = STATUSES.includes(block.status) ? block.status : 'not_offered';
  const location = parseLatLng(block.location);

  return {
    status,
    areaName: typeof block.area?.name === 'string' ? block.area.name : null,
    missingForMinimum: positiveOrNull(block.missing_for_minimum),
    missingForFree: positiveOrNull(block.missing_for_free),
    location: location
      ? { ...location, address: typeof block.location.address === 'string' ? block.location.address : '' }
      : null,
  };
}

/**
 * Where the order is going, as the server has it: the `feather_extras.delivery` location,
 * or — on a WooCommerce GET that predates the block — the order meta the server writes
 * alongside it (`_feather_delivery_lat` / `_lng` / `_address`). Null for none.
 */
export function parseOrderDeliveryLocation(order: any): DeliveryLocation | null {
  const fromQuote = parseDeliveryQuote(order)?.location;
  if (fromQuote) return fromQuote;

  const meta = order?.meta_data;
  const pin = parseLatLng({
    lat: metaValue(meta, '_feather_delivery_lat'),
    lng: metaValue(meta, '_feather_delivery_lng'),
  });
  if (!pin) return null;
  const address = metaValue(meta, '_feather_delivery_address');
  return { ...pin, address: typeof address === 'string' ? address : '' };
}

/**
 * Whether the server's location is the pin on screen. Within ~1 cm: the server rounds to
 * seven decimals, so an exact comparison would read every pin as unconfirmed.
 */
export function samePin(a: LatLng | null | undefined, b: LatLng | null | undefined): boolean {
  if (!a || !b) return false;
  return Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;
}

/** `{lat, lng}` from anything shaped like it, or null. Strings are accepted; NaN is not. */
export function parseLatLng(value: any): LatLng | null {
  if (!value || typeof value !== 'object') return null;
  const lat = parseFloat(value.lat);
  const lng = parseFloat(value.lng);
  if (!isFinite(lat) || !isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) {
    return null;
  }
  return { lat, lng };
}

function metaValue(meta: any, key: string): any {
  if (!Array.isArray(meta)) return undefined;
  return meta.find((m: any) => m?.key === key)?.value;
}

function positiveOrNull(value: any): number | null {
  const n = parseFloat(value);
  return isNaN(n) || n <= 0 ? null : n;
}
